<script lang="ts" setup>
import {
  LoginData,
  emailRules,
  passwordRules,
  digitRules,
} from '@/models/login.model';
import { Actions, ShiftCMSState } from '@/store';
import { UsersActions } from '@/stores/users.store';
import { Ref, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState>();
const router = useRouter();

const valid = ref<boolean>(true);
const twoFactorEnabled = ref<boolean>(false);
const twoFactorRequired = ref<boolean>(false);
const digits = ref<string>('');
const loginData = ref<LoginData>({
  email: '',
  password: '',
});

const qrCode = ref<string>('');
const message = ref<string>('');

const enable2FA = async () => {
  const result = await store.dispatch(
    UsersActions.Enable2FACold,
    loginData.value,
  );

  qrCode.value = result.qrCodeDataUrl;
};

const verify2FA = async (isActive: Ref) => {
  const result = await store.dispatch(UsersActions.Verify2FACold, {
    token: digits.value,
    ...loginData.value,
  });
  if (result.twoFactorEnabled) {
    isActive.value = false;
    store.dispatch(Actions.GetCurrentUser);
    message.value = '';
    twoFactorRequired.value = false;
    twoFactorEnabled.value = true;
    store.dispatch(Actions.ShowNotification, {
      message: result.message,
      type: 'success',
    });
  } else {
    message.value = result.message;
  }
};

const login = async () => {
  if (valid.value && digits.value) {
    const data = await store.dispatch(Actions.LoginTwoFactor, {
      ...loginData.value,
      token: digits.value,
    });

    if (data.loggedIn) {
      router.push('/');
      return;
    }
  }

  if (valid.value) {
    const data = await store.dispatch(Actions.Login, loginData.value);

    if (data.loggedIn) {
      router.push('/');
    } else if (data.twoFactorEnabled) {
      twoFactorEnabled.value = true;
    } else if (data.twoFactorRequired) {
      twoFactorRequired.value = true;
    }
  }
};
</script>
<template>
  <v-card width="50%" class="mx-auto login-form" elevation="4">
    <v-form v-model="valid" @submit.prevent="login">
      <v-container>
        <h2>{{ $t('login.header') }}</h2>
        <v-row>
          <v-col md="8" class="mx-auto">
            <v-text-field
              v-model="loginData.email"
              :rules="emailRules"
              label="E-mail"
              required
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col md="8" class="mx-auto">
            <v-text-field
              v-model="loginData.password"
              :rules="passwordRules"
              :label="$t('login.password')"
              type="password"
              required
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row v-if="twoFactorEnabled">
          <v-col md="8" class="mx-auto">
            <v-text-field
              v-model="digits"
              :rules="digitRules"
              :label="$t('login.twoFactorCode')"
              type="text"
              required
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row v-if="twoFactorRequired">
          <v-col md="2" class="mx-auto"></v-col>
          <v-col md="6" class="mx-auto">
            <v-alert color="info"
              >Two Factor is required now to login in as admin. For starters
              make sure you have an Authenticator app on your phone. Then click
              the button to continue.
            </v-alert>
          </v-col>
          <v-col md="4" class="mx-auto">
            <v-dialog width="auto" v-if="twoFactorRequired">
              <template v-slot:activator="{ props: activatorProps }">
                <v-btn
                  v-bind="activatorProps"
                  class="mt-11"
                  type="button"
                  color="info"
                  @click="enable2FA"
                  >Enable 2FA</v-btn
                >
              </template>
              <template v-slot:default="{ isActive }">
                <v-card>
                  <v-card-text>
                    <h2>Enable Two Factor Authentication</h2>
                    <p>
                      <strong>Scan</strong> the following QR code with your
                      authenticator app.
                    </p>
                    <img
                      :src="qrCode"
                      alt="2FA QR code"
                      style="margin: 0 auto; display: block"
                    />
                    <p>
                      Provide the 6 digit code and enable two factor
                      authentication on your account.
                    </p>
                    <v-row>
                      <v-col
                        ><v-text-field v-model="digits" label="6 Digit Code"
                      /></v-col>
                      <v-col>
                        <v-btn
                          class="mt-2"
                          color="success"
                          type="button"
                          block
                          @click="() => verify2FA(isActive)"
                          >Enable 2FA</v-btn
                        ></v-col
                      >
                    </v-row>
                    <v-alert color="warning" v-if="message">{{
                      message
                    }}</v-alert>
                  </v-card-text>
                </v-card>
              </template>
            </v-dialog>
          </v-col>
        </v-row>
        <v-row>
          <v-col col="2"></v-col>
          <v-col>
            <v-btn
              class="mt-2"
              type="submit"
              block
              v-text="$t('login.submit')"
            ></v-btn>
          </v-col>
          <v-col col="2"></v-col>
        </v-row>
      </v-container>
    </v-form>
  </v-card>
</template>
<style scoped>
.login-form {
  margin-top: 80px;
}
</style>
